import React, { useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { Button, Row, Col, Form } from 'react-bootstrap';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import iconMarker from 'leaflet/dist/images/marker-icon.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix the default marker icon paths under webpack bundling.
const markerIcon = L.icon({
  iconUrl: iconMarker,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Fallback center when no pin is set yet (Bangkok). Admin can pan/search from here.
const DEFAULT_CENTER = [13.7563, 100.5018];

function ClickToPlace({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

/**
 * Interactive map for choosing a building's coordinates.
 * Props:
 *   lat, lng : current values (number | '' | null)
 *   onChange(lat, lng) : called with numbers, or (null, null) when cleared
 */
export default function DormLocationPicker({ lat, lng, onChange }) {
  const mapRef = useRef(null);
  const [locating, setLocating] = useState(false);

  const hasPin = lat !== '' && lat !== null && lat !== undefined
    && lng !== '' && lng !== null && lng !== undefined;
  const position = hasPin ? [Number(lat), Number(lng)] : null;

  const round = (n) => Math.round(n * 1e7) / 1e7; // ~1cm precision, matches DECIMAL(10,7)

  const handlePick = (la, ln) => onChange(round(la), round(ln));

  const handleManual = (field, value) => {
    if (field === 'lat') onChange(value === '' ? '' : value, lng);
    else onChange(lat, value === '' ? '' : value);
  };

  const handleLocate = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const la = round(pos.coords.latitude);
        const ln = round(pos.coords.longitude);
        onChange(la, ln);
        if (mapRef.current) mapRef.current.flyTo([la, ln], 18);
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleClear = () => onChange('', '');

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Form.Label className="mb-0"><strong>ตำแหน่งบนแผนที่</strong></Form.Label>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" size="sm" onClick={handleLocate} disabled={locating}>
            {locating ? 'กำลังหาตำแหน่ง...' : '📍 ใช้ตำแหน่งปัจจุบัน'}
          </Button>
          {hasPin && (
            <Button variant="outline-secondary" size="sm" onClick={handleClear}>ล้างพิกัด</Button>
          )}
        </div>
      </div>

      <div style={{ height: 300, borderRadius: 8, overflow: 'hidden' }}>
        <MapContainer
          center={position || DEFAULT_CENTER}
          zoom={position ? 17 : 6}
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
        >
          <TileLayer
            attribution="&copy; OpenStreetMap"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
          />
          <ClickToPlace onPick={handlePick} />
          {position && (
            <Marker
              position={position}
              icon={markerIcon}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const { lat: la, lng: ln } = e.target.getLatLng();
                  handlePick(la, ln);
                },
              }}
            />
          )}
        </MapContainer>
      </div>

      <Form.Text className="text-muted d-block mt-1">
        คลิกบนแผนที่เพื่อวางหมุด หรือใช้ตำแหน่งปัจจุบัน แล้วลากหมุดปรับให้ตรงอาคาร
      </Form.Text>

      <Row className="mt-2">
        <Col xs={6}>
          <Form.Label className="small mb-1">ละติจูด (lat)</Form.Label>
          <Form.Control
            type="number"
            step="0.0000001"
            value={lat ?? ''}
            onChange={(e) => handleManual('lat', e.target.value)}
            placeholder="เช่น 13.7563"
          />
        </Col>
        <Col xs={6}>
          <Form.Label className="small mb-1">ลองจิจูด (lng)</Form.Label>
          <Form.Control
            type="number"
            step="0.0000001"
            value={lng ?? ''}
            onChange={(e) => handleManual('lng', e.target.value)}
            placeholder="เช่น 100.5018"
          />
        </Col>
      </Row>
    </div>
  );
}
